/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The params the first-party event stream keeps, key → max string length.
 * Everything else is dropped, so the payload stays identifier-free: these are
 * content and behaviour dimensions, not identity. This short allowlist is what
 * lets search terms, provider slugs, rank-at-click, filter usage and
 * impression lists survive in the one sink that is not consent-gated (GA4 is).
 *
 * ONE list for both ends: the client picks the params it sends with it
 * (pickPingParams in src/lib/gtag.ts) and the server keeps exactly these
 * (parseExtra in server/routes/siteStats.ts, which also refuses a free query on
 * any other key). Until 2026-10-14 each end kept its own copy, pinned equal by
 * a source scan. The admin keeps no copy: the Events drill-down offers the keys
 * an event really carries (paramKeys of event-detail, read off this list), and
 * the params route refuses any key that is not on it (parseParamSpec in
 * server/routes/siteStats.ts).
 *
 * Plain ESM JavaScript with zero imports: the client bundle and the server
 * build both import it.
 */

/** @type {Readonly<Record<string, number>>} */
export const EVENT_PING_PARAM_KEYS = Object.freeze({
  search_term: 80,
  result_count: 80,
  provider_slug: 80,
  position: 80,
  filter_name: 80,
  filter_value: 80,
  // results_view/multi_compare_view impression list — up to 10 comma-joined
  // slugs; provider slugs can reach ~64 chars (name + id suffix), so the cap
  // must hold a realistic full page. Overflow cuts at a comma boundary (both
  // ends) so a truncated list never carries a corrupted slug.
  slugs: 400,
  percent: 80,
  variant: 80,
  article_slug: 80,
  // Signup-form funnel: which wizard step an event belongs to.
  step: 80,
  // Particulier / Zakelijk / Alle — which segment the results were read as.
  audience: 80,
  // Selected looptijd ('1 jaar', 'Alle', …) on filter and results events.
  contract_duration: 80,
  // Which kind of page an event happened on (pageCategoryForPath).
  page_category: 80,
  // The choice a visitor made in a branching UI (e.g. the MID-checker path).
  decision: 80,
  // Size of the result set behind a results_view — the CTR denominator.
  provider_count: 80,
  // Kennisbank listing: the category chip picked (an id or `all`) and the
  // page number clicked. Both are URL state, which pageviews never carry.
  category: 80,
  page_number: 80,
  // Shortlist size on compare_select / compare_remove / compare_tray_click / compare_clear —
  // the aanbiedersoverzicht already sent it, unlisted.
  selected_count: 80,
  // The installer-partner report (server/data/partnerOutboundReport.ts):
  // where a CTA sat (mid_checker_installer_cta's `no_charger` / `result_no`;
  // the toggles and the compare CTAs carry it too), which installer a click
  // went to, and the province page it came from (an id, or `all` on the hub).
  from: 80,
  installer: 80,
  province: 80,
  // page_leave (armPageEngaged): the visit's cumulative foreground seconds —
  // the site's per-page time AND the partner portal's profile dwell
  // (server/data/providerStats.ts). Numbers only on the server: a string is
  // dropped, a number rounded and clamped to 0..1200. Its scroll depth travels
  // as `percent`, listed above. (`dwell_s`, the number on the retired
  // page_dwell row, left the allowlist on 2026-10-02: page_leave is the one
  // exit marker, and a page_dwell an old bundle still sends counts nowhere.)
  seconds: 80,
});
